<template>
  <span
    ref="input"
    role="spinbutton"
    :contenteditable="!disabled"
    :data-segment="type"
    :data-placeholder="value === null"
    :tabindex="disabled ? -1 : 0"
    :aria-label="type"
    :aria-valuenow="value ?? undefined"
    :aria-valuetext="ariaValueText"
    :aria-valuemin="min"
    :aria-valuemax="max"
    :style="{ caretColor: 'transparent' }"
    inputmode="numeric"
    autocorrect="off"
    spellcheck="false"
    enterkeyhint="next"
    class="select-none rounded px-1 tabular-nums outline-none focus:bg-blue-500 focus:text-white"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @beforeinput="handleBeforeInput"
    >{{ seg.displayValue.value }}</span
  >
</template>

<script setup lang="ts">
import { useTemplateRef } from "vue";

import { useSegment } from "./useSegment";

const props = defineProps<{
  type: "hour" | "minute";
  value: number | null;
  min: number;
  max: number;
  ariaValueText?: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  change: [value: number | null];
  increment: [delta: number];
  next: [];
  prev: [];
}>();

const input = useTemplateRef("input");

const seg = useSegment({
  value: () => props.value,
  min: props.min,
  max: props.max,
  digits: 2,
  disabled: () => props.disabled ?? false,
});

const focus = (): void => {
  input.value?.focus();
};

const handleFocus = (): void => {
  seg.resetBuffer();

  const selection = window.getSelection();
  if (selection && input.value) {
    selection.collapse(input.value);
  }
};

const handleBlur = (): void => {
  seg.resetBuffer();
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (props.disabled) {
    return;
  }

  switch (event.key) {
    case "ArrowLeft": {
      event.preventDefault();
      emit("prev");
      seg.resetBuffer();
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      emit("next");
      seg.resetBuffer();
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      emit("increment", 1);
      seg.resetBuffer();
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      emit("increment", -1);
      seg.resetBuffer();
      break;
    }
    case "Backspace":
    case "Delete": {
      event.preventDefault();
      const result = seg.handleBackspace();
      emit("change", result.value);
      if (result.navigatePrev) {
        emit("prev");
      }
      break;
    }
  }
};

const handleBeforeInput = (event: Event): void => {
  event.preventDefault();

  if (props.disabled) {
    return;
  }

  const ev = event as InputEvent;
  const data = ev.data;
  switch (data) {
    case "0":
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
    case "7":
    case "8":
    case "9": {
      const result = seg.handleDigitInput(data);
      emit("change", result.value);
      if (result.advance) {
        emit("next");
      }
      break;
    }
  }
};

defineExpose({
  focus,
  el: input,
});
</script>
